// src/features/wallet/WalletWorkspacePage.tsx
import { useParams } from 'react-router-dom';

import { LoadingBoundary, PageContainer, PageProgress } from '../../app/components/foundation';
import {
  WalletWorkspaceHeader,
  WalletInfoCard,
  WalletBalanceCard,
  WalletTransactionsCard,
} from './components';
import { useWallet } from './hooks/useWallet';
import './styles/wallet.css';

/**
 * Wallet Workspace Foundation (/wallet/:id). Same shell pattern as
 * CommissionWorkspacePage/OrderWorkspacePage: PageContainer +
 * feature-owned header, card grid below, RTL/Persian throughout,
 * existing tokens/primitives only.
 *
 * Reads `id` from the route params and resolves everything through
 * useWalletWorkspaceMock(id) — the wallet record plus its
 * transaction/payment/timeline, all still mock data from
 * data/wallet.mock.ts. No API calls; useWalletWorkspaceMock is the
 * seam a later phase swaps for a real wallet-detail hook without
 * touching this file or the cards below it (same shape).
 */
export function WalletWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const { wallet, loading, error } = useWallet(id);

  if (loading && !wallet) return <PageContainer><LoadingBoundary loading /></PageContainer>;
  if (error) return <PageContainer><LoadingBoundary loading={false} error={error} /></PageContainer>;

  if (!wallet) {
    return (
      <PageContainer className="flex flex-col gap-6">
        <p className="vania-wallet-workspace-empty">کیف پول مورد نظر یافت نشد.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="flex flex-col gap-6">
      <PageProgress active={loading} />
      <WalletWorkspaceHeader wallet={wallet} />

      <div className="vania-wallet-workspace-grid">
        <WalletInfoCard wallet={wallet} />
        <WalletBalanceCard wallet={wallet} />
        <WalletTransactionsCard transactions={wallet.transactions ?? []} />
      </div>
    </PageContainer>
  );
}
